import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { AlertTriangle, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Reusable custom confirm dialog (replaces native window.confirm).
 *
 * Props:
 * - open: boolean
 * - title, description: strings
 * - confirmText, cancelText: optional labels
 * - destructive: bool (red confirm button)
 * - loading: bool
 * - onConfirm, onCancel: callbacks
 */
export default function ConfirmDialog({
  open,
  title,
  description,
  confirmText,
  cancelText,
  destructive = false,
  loading = false,
  onConfirm,
  onCancel,
}) {
  const { t } = useLanguage();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onCancel}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${destructive ? 'bg-red-500/15 text-red-400' : 'bg-primary/15 text-primary'}`}>
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-base">{title}</h3>
                  {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
                </div>
                <button onClick={onCancel} className="text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={onCancel} disabled={loading}>
                  {cancelText || t('common', 'cancel') || 'Отмена'}
                </Button>
                <Button
                  onClick={onConfirm}
                  disabled={loading}
                  className={destructive ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-primary hover:bg-primary/90'}
                >
                  {loading ? '...' : (confirmText || t('common', 'confirm') || 'Подтвердить')}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
